'use client';

import styles from './Activities.module.css';
import { ReactNode, useContext, useEffect, useState, type FC } from 'react';
import { Flex, Pagination } from '@/_components';
import { ActivitiesEmpty } from './_components/ActivitiesEmpty';
import { ActivitiesList } from './_components/ActivitiesList';
import { ActivitiesLoading } from './_components/ActivitiesLoading';
import { UserContext } from '@/_contexts/UserProvider';
import { getActivities } from '@/_api/admin-activities';
import { ToastContext } from '@/_contexts/ToastProvider';
import { Activity } from '@/_types/models/activitites';
import { ACTIVITY_LOG_CODES } from '@/_constants/activity_logs';
import type { ActivitiesProps } from './Activities.types';

export const Activities: FC<ActivitiesProps> = ({
  companyId,
  isPending,
  enablePagination,
  type = '',
  reviewId: _,
  questionnaireId,
}) => {
  const { token, isAdvisor } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [page, setPage] = useState<number>(0);
  const [isLoading, setLoading] = useState(false);
  const [activities, setActivities] = useState<Array<Activity>>([]);
  const [pageCount, setPageCount] = useState<number>(0);

  useEffect(() => {
    setPage(0);
  }, [type]);

  useEffect(() => {
    if (!token || !isAdvisor) return;

    const _loadActivityLogs = async () => {
      try {
        const typeId = type
          ? ACTIVITY_LOG_CODES[type as keyof typeof ACTIVITY_LOG_CODES]
          : undefined;

        setLoading(true);

        const { activities, total, per_page } = await getActivities({
          company_id: companyId,
          questionnaire_id: questionnaireId!,
          type_id: typeId,
          page,
          token,
        });

        setPageCount(Math.ceil(total / per_page) || 0);
        setActivities(activities);
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoading(false);
      }
    };

    _loadActivityLogs();

    return () => {
      setActivities([]);
      setLoading(true);
    };
  }, [addToast, type, page, companyId, isAdvisor, questionnaireId, token]);

  const getActivityState = (): ReactNode => {
    if (isLoading || isPending) {
      return (
        <Flex isColumn={true} gap={4}>
          <ActivitiesLoading />
          <ActivitiesLoading />
          <ActivitiesLoading />
        </Flex>
      );
    }
    if (!activities?.length) return <ActivitiesEmpty />;

    return <ActivitiesList activities={activities} />;
  };

  const activityLogs = getActivityState();

  const onNext = () => {
    setPage(page + 1);
  };

  const onPrevious = () => {
    setPage(page - 1);
  };

  return (
    <Flex isColumn={true}>
      <Flex isColumn={true} className={styles.activities}>
        {activityLogs}
      </Flex>
      {enablePagination && (
        <Flex
          className={styles.activitiesPagination}
          isCentered={true}
          padding={12}
        >
          <Pagination
            onNext={onNext}
            onPrevious={onPrevious}
            activePage={page}
            pageCount={pageCount}
          />
        </Flex>
      )}
    </Flex>
  );
};
